export interface User {
  id: string;
  provider: 'google' | 'telegram' | 'dev';
  name: string;
  // exactOptionalPropertyTypes: backend may legitimately return null for these,
  // and we coalesce to undefined on read. Allow explicit undefined assignment.
  email?: string | undefined;
  username?: string | undefined;
  /** Admin impersonate state (mig 0024). When set, this session was minted
   *  by an admin via the magic-link flow — banner must render and WRITE
   *  actions will be 403'd by the backend. Cleared after stop. */
  impersonatorAdminId?: string | null | undefined;
  impersonatorExpiresAt?: string | null | undefined;
  /** Account on hold (admin). Reads still work; the dashboard says so instead
   *  of letting the first payment attempt fail with a bare 403. */
  suspended?: boolean | undefined;
}

type Listener = (user: User | null) => void;

const STORAGE_KEY = 'roamzy_user_fallback';

let current: User | null = null;
/** True once Auth.init() has answered at least once. Until then "no user"
 *  means "not known yet", not "guest" — the site banner for guests waits
 *  for it so a signed-in visitor never sees it flash. */
let resolved = false;
const listeners = new Set<Listener>();

// AUTH_BROADCAST_V1 — F-010 cross-tab sync. When tab A logs out, tab B's
// channel listener wipes its in-memory user and re-resolves the router.
const authChannel: BroadcastChannel | null = (typeof BroadcastChannel !== 'undefined')
  ? new BroadcastChannel('roamzy-auth')
  : null;
if (authChannel) {
  authChannel.addEventListener('message', (e: MessageEvent<{ type: 'login' | 'logout' }>) => {
    if (!e.data) return;
    if (e.data.type === 'logout') {
      current = null;
      writeFallback(null);
      emit();
    } else if (e.data.type === 'login') {
      // Re-fetch /api/me to learn who's signed in over there.
      void Auth.init();
    }
  });
}

function readFallback(): User | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

function writeFallback(user: User | null): void {
  if (user) localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
  else localStorage.removeItem(STORAGE_KEY);
}

function emit(): void {
  for (const fn of listeners) fn(current);
  document.body.dataset.auth = current ? '1' : '';
  // Grammarly / LanguageTool / Microsoft Editor extensions ride on form
  // / textarea presence and scan the WHOLE page once they activate.
  // Logged-in pages (top-up modal, settings tab, etc.) trigger that
  // activation, then the extension overlays grey strikethrough markers
  // on big-text elements (hero numbers, dashboard pills) — operator
  // reported 2026-05-08 «полосы появляются только у залогиненных».
  // Setting data-gramm=false at body level tells Grammarly to skip the
  // entire page. Per-element fixes from earlier stay as belt-and-braces.
  if (current) {
    document.body.setAttribute('data-gramm', 'false');
    document.body.setAttribute('data-gramm_editor', 'false');
    document.body.setAttribute('data-lt-tmp', '0');
  } else {
    document.body.removeAttribute('data-gramm');
    document.body.removeAttribute('data-gramm_editor');
    document.body.removeAttribute('data-lt-tmp');
  }
}

export const Auth = {
  async init(): Promise<void> {
    try {
      // B-005: backend returns { user: User | null }
      const res = await fetch('/api/me', { credentials: 'include' });
      if (res.ok) {
        const json = (await res.json()) as {
          user: {
            id: string;
            displayName: string;
            email: string | null;
            impersonatorAdminId?: string | null;
            impersonatorExpiresAt?: string | null;
            suspended?: boolean;
          } | null;
        };
        if (json.user) {
          current = {
            id: json.user.id,
            provider: 'dev',
            name: json.user.displayName,
            email: json.user.email ?? undefined,
            impersonatorAdminId: json.user.impersonatorAdminId ?? null,
            impersonatorExpiresAt: json.user.impersonatorExpiresAt ?? null,
            suspended: json.user.suspended === true,
          };
          // Clear the localStorage fallback now that the server confirmed identity.
          writeFallback(null);
          resolved = true;
          emit();
          return;
        }
      }
    } catch {
      /* offline / backend not running */
    }
    current = readFallback();
    resolved = true;
    emit();
  },

  getUser(): User | null {
    return current;
  },

  isAuthed(): boolean {
    return current !== null;
  },

  /** Whether the first /api/me answer (or its offline fallback) is in. */
  isResolved(): boolean {
    return resolved;
  },

  async loginDev(userIdOrName: string): Promise<User> {
    let user: User;
    try {
      // B-005: POST /api/auth/dev-login. Backend creates user if needed, sets
      // HttpOnly session cookie, returns user. ULID-shaped userIds are reused;
      // anything else (e.g. "guest-1234567") becomes a freshly-issued ULID
      // with the input as displayName.
      const isUlid = /^[0-9A-HJKMNP-TV-Z]{26}$/.test(userIdOrName);
      const baseBody: Record<string, unknown> = isUlid
        ? { userId: userIdOrName }
        : { displayName: userIdOrName };
      // Lazy-import to avoid a hard cycle (utm doesn't depend on auth, but
      // keeping this dynamic keeps the module graph clean).
      const { authPayloadExtras } = await import('./utm');
      const body = { ...baseBody, ...authPayloadExtras() };
      const res = await fetch('/api/auth/dev-login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { user: { id: string; displayName: string; email: string | null } };
      user = {
        id: json.user.id,
        provider: 'dev',
        name: json.user.displayName,
        email: json.user.email ?? undefined,
      };
    } catch {
      // Backend not reachable (offline dev) — local-only fallback.
      user = { id: userIdOrName, provider: 'dev', name: `Dev ${userIdOrName}` };
      writeFallback(user);
    }
    current = user;
    emit();
    authChannel?.postMessage({ type: 'login' });
    return user;
  },

  async logout(): Promise<void> {
    try {
      // DELETE /api/auth/session deletes the server-side session row and
      // sends Clear-Cookie. We always also clear the local fallback below.
      await fetch('/api/auth/session', { method: 'DELETE', credentials: 'include' });
    } catch {
      /* fall through to local clear */
    }
    writeFallback(null);
    current = null;
    emit();
    authChannel?.postMessage({ type: 'logout' });
  },

  onChange(fn: Listener): () => void {
    listeners.add(fn);
    fn(current);
    return () => listeners.delete(fn);
  },

  /**
   * Patch the in-memory user record. Used after a successful PATCH /api/me
   * (e.g. Settings email edit, top-up modal email collection) so the UI
   * reflects the change without a full /api/me round-trip. The server is
   * still authoritative — Auth.init() on next page load re-syncs.
   */
  updateUserLocal(patch: Partial<User>): void {
    if (!current) return;
    current = { ...current, ...patch };
    emit();
  },
};
